/**
 * Flow Optimizer formula — AST -> text serializer
 * For Media Devoted | Created By Ciro Autiero
 *
 * Operator-precedence-aware serializer. Round-trips through `parseFormula`
 * so the canonical text form survives mode-switching in the UI.
 */

import type { AstNode } from "./ast";

const PRECEDENCE: Record<string, number> = {
  "+": 1,
  "-": 1,
  "*": 2,
  "/": 2,
  "%": 2,
  unary: 3,
  atom: 4,
};

export function serializeFormula(node: AstNode | null): string {
  if (!node) return "";
  return write(node, 0);
}

function write(node: AstNode, parentPrec: number): string {
  if (node.kind === "num") return formatNumber(node.value);
  if (node.kind === "ident") return node.name;
  if (node.kind === "call") return writeCall(node);
  if (node.kind === "unary") return writeUnary(node, parentPrec);
  return writeBinary(node, parentPrec);
}

function writeBinary(
  node: Extract<AstNode, { kind: "binary" }>,
  parentPrec: number,
): string {
  const prec = PRECEDENCE[node.op];
  const left = write(node.left, prec);
  const right = write(node.right, prec + 1);
  const expr = `${left} ${node.op} ${right}`;
  return prec < parentPrec ? `(${expr})` : expr;
}

function writeUnary(
  node: Extract<AstNode, { kind: "unary" }>,
  parentPrec: number,
): string {
  const inner = write(node.operand, PRECEDENCE.unary);
  const expr = `-${inner}`;
  return PRECEDENCE.unary < parentPrec ? `(${expr})` : expr;
}

function writeCall(node: Extract<AstNode, { kind: "call" }>): string {
  const args = node.args.map((a) => write(a, 0)).join(", ");
  return `${node.name}(${args})`;
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return Number(value.toFixed(6)).toString();
}
